/**
 * "quiz" / "quizzesQuiz" / "trueFalse" / "missingLetter" / "missingWord" /
 * "math": a question (text, optional image) with tappable answers.
 *
 * Ported from PlayspaceQuiz.cs, QuizspaceQuiz.cs, PlayspaceTrueFalse.cs,
 * PlayspaceMissingLetter.cs, PlayspaceMissingWord.cs, PlayspaceMath.cs and
 * AnswerElementPlayer.cs:
 *  - selectAllCorrects: the slide ends when every correct answer is selected;
 *    otherwise it ends on the first correct answer.
 *  - alwaysCorrect: a wrong tap just marks the answer and play continues;
 *    when false (or in a quiz project) the first wrong tap ends the slide.
 *  - Quiz projects colour the answers orange/purple/blue/red; trueFalse
 *    colours them blue (true) / red (false); games show plain cards, or no card
 *    at all for text-only answers.
 *  - When the slide ends in a quiz project, correct answers turn green and the
 *    others red (faded when not tapped).
 */
import { useMemo, useState, type CSSProperties, type ReactNode } from "react";
import type { Element as El, QuizElement, SlideOf } from "@makeit/schema";
import { libraryUrl } from "../../data/media";
import { ElementCard, ElementImage, TextBox, useElementSound } from "../elements";
import { usePlayer } from "../context";
import { COLOR_BLUE, COLOR_GREEN, COLOR_RED, QUIZ_COLORS, useActivity } from "../activity";
import { shuffled } from "../util";

export type QuizLike =
  | SlideOf<"quiz">
  | SlideOf<"quizzesQuiz">
  | SlideOf<"trueFalse">
  | SlideOf<"missingLetter">
  | SlideOf<"missingWord">
  | SlideOf<"math">;

function answersOf(slide: QuizLike): QuizElement[] {
  return "quizElements" in slide ? slide.quizElements : slide.answers;
}

export function QuizSlide({ slide, header, onEnd }: { slide: QuizLike; header?: ReactNode; onEnd?: () => void }) {
  const { project, timeUp } = usePlayer();
  const all = answersOf(slide);
  const answers = useMemo(() => (project.settings.randomOrderElements ? shuffled(all) : all), [all, project.settings.randomOrderElements]);
  const selectAll = slide.type === "quiz" || slide.type === "quizzesQuiz" ? slide.selectAllCorrects : false;
  const alwaysCorrect = "alwaysCorrect" in slide ? slide.alwaysCorrect : true;
  const correctTotal = all.filter((a) => a.correct).length;
  const activity = useActivity(selectAll ? Math.max(1, correctTotal) : 1);
  const [picked, setPicked] = useState<Record<string, "correct" | "wrong">>({});
  const [endedByTap, setEnded] = useState(false);
  const ended = endedByTap || timeUp;

  const colored = activity.quizMode || slide.type !== "quiz";
  const colorFor = (a: QuizElement, i: number) => (slide.type === "trueFalse" ? (a.isTrue ? COLOR_BLUE : COLOR_RED) : QUIZ_COLORS[i % 4]);

  const tap = (a: QuizElement) => {
    if (ended || picked[a.id]) return;
    setPicked((p) => ({ ...p, [a.id]: a.correct ? "correct" : "wrong" }));
    if (a.correct) activity.correct(a.text.content);
    else activity.wrong(alwaysCorrect, a.text.content);
    if (activity.isDone()) {
      setEnded(true);
      onEnd?.();
    }
  };

  const hasImage = !!slide.image?.image?.fileName;
  const question = "question" in slide ? slide.question : null;

  return (
    <div className={`quiz ${activity.quizMode ? "quiz-project" : ""} ${hasImage ? "has-image" : ""} type-${slide.type}`}>
      {hasImage && slide.image && (
        <div className="quiz-image">
          <ElementImage element={slide.image} />
        </div>
      )}
      <div className="quiz-question">{header ?? (question && <QuestionView element={question} />)}</div>
      <div className={`quiz-answers count-${answers.length}`}>
        {answers.map((a, i) => {
          const state = picked[a.id];
          let cls = "quiz-answer";
          let style: CSSProperties | undefined;
          if (colored) {
            cls += " colored";
            style = { background: colorFor(a, i) };
            if (ended && activity.quizMode) {
              style = { background: a.correct ? COLOR_GREEN : COLOR_RED, opacity: a.correct || state ? 1 : 0.3 };
            }
          } else {
            cls += " bare";
          }
          if (state) cls += ` ${state}`;
          return (
            <ElementCard key={a.id} element={a} className={cls} style={style} onClick={() => tap(a)} bg={colored ? undefined : "none"}>
              {state && <AnswerMark state={state} quiz={activity.quizMode} />}
            </ElementCard>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Tick / cross over a tapped answer (AnswerElementPlayer › Correct / Wrong):
 * quiz projects use the translucent marks of QuizAnswerElement, games the
 * opaque 128 px ones of PlayerAnswerElement.
 */
export function AnswerMark({ state, quiz }: { state: "correct" | "wrong"; quiz: boolean }) {
  const name = quiz ? (state === "correct" ? "correctMark" : "wrongMark") : state === "correct" ? "answerYes" : "answerNo";
  return <img className={`answer-mark ${state} ${quiz ? "quiz" : "game"}`} src={libraryUrl(`ui/${name}.${name === "answerNo" ? "gif" : "png"}`)} alt="" draggable={false} />;
}

export function QuestionView({ element, children }: { element: El | null; children?: ReactNode }) {
  const { play, hasSound } = useElementSound(element);
  const empty = !element?.text.content && !children;
  return (
    <div className={`quiz-question-text ${hasSound ? "has-sound" : ""} ${empty ? "empty" : ""}`} onClick={hasSound ? play : undefined}>
      {element && <TextBox text={element.text} fit />}
      {children}
      {hasSound && <span className="sound-badge" aria-hidden>♪</span>}
    </div>
  );
}
